import { HttpClient } from '@angular/common/http';
import { AppwriteDatabaseService } from '@prism/angular-shared';
import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { AppwriteStorageService, CompatUploadSnapshot } from '@prism/angular-shared';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ConfirmationDialogService, DialogReturn, DialogReturnType, DialogService, SnackbarService, SnackbarStatus } from '@prism/angular-shared';
import { EditSoftwareRequestDialogArgs, EditSoftwareRequestDialogReturnValue, PrismCommonSubModule, PrismModule, SoftwareRequest, SoftwareRequestStatus, StorageFolder, UploadedFile, UploadedFileType, User } from '@prism/prism-shared';
import { resizeImage } from 'libs/angular-shared/src/lib/components/image-uploader/resize-image.util';
import { BehaviorSubject, defaultIfEmpty, filter, first, forkJoin, from, map, Observable, of, pipe, startWith, Subject, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs';
import { CoreSelectors } from '../../state/core';

enum MarkdownView {
  Markdown = 'markdown',
  Split = 'split',
  Preview = 'preview',
}

@Component({
  selector: 'prism-edit-software-request-dialog',
  templateUrl: './edit-software-request-dialog.component.html',
  styleUrls: ['./edit-software-request-dialog.component.scss'],
})
export class EditSoftwareRequestDialogComponent implements OnInit, OnDestroy {
  @Input() submitPressed$!: Subject<void>
  @Input() args!: EditSoftwareRequestDialogArgs;

  public MarkdownView = MarkdownView
  SoftwareRequestStatus = SoftwareRequestStatus;

  public markdownView$: BehaviorSubject<MarkdownView> = new BehaviorSubject<MarkdownView>(MarkdownView.Split);

  public formGroup: FormGroup<{
    title: FormControl<string | null>;
    description: FormControl<string | null>;
  }> = new FormGroup({
    title: new FormControl<string>('', Validators.required),
    description: new FormControl<string>(''),
  });

  public currentUser$!: Observable<User>;
  public userCanEdit$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public files$: BehaviorSubject<UploadedFile[]> = new BehaviorSubject<UploadedFile[]>([]);

  public requestId: string = '';
  public storageBucket = StorageFolder.softwareRequests;

  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store,
    private dialog: DialogService<EditSoftwareRequestDialogArgs>,
    private appwrite: AppwriteDatabaseService,
    private confirmationDialog: ConfirmationDialogService,
    private http: HttpClient,
    private storage: AppwriteStorageService,
    private snackbarService: SnackbarService,
    private elementRef: ElementRef,
  ) { }

  ngOnInit(): void {

    this.currentUser$ = this.store.select(CoreSelectors.getCurrentUser).pipe(

      filter((user): user is User => !!user)

    );

    this.store.select(CoreSelectors.getCurrentUser).pipe(
      filter(user => !!user),
      first(),
      tap(user => {
        const request = this.args?.softwareRequest;

        if (!!request) {
          this.requestId = request.id;

          this.formGroup.patchValue({
            title: request.title ?? '',
            description: request.description ?? '',
          });

          const canEditByRole = user.abilities[PrismModule.PrismCommon][PrismCommonSubModule.SoftwareRequests].CanEdit;

          const canEditByOwnership = user.id === request.createdById;

          const isWaiting = request.status === SoftwareRequestStatus.Waiting

          const userCanEdit = (canEditByRole || canEditByOwnership) && isWaiting;
          this.userCanEdit$.next(userCanEdit);

          if (!userCanEdit) {
            this.markdownView$.next(MarkdownView.Preview);
          }

        } else {
          this.requestId = this.appwrite.createId();
          this.userCanEdit$.next(true);
        }
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe();

    this.formGroup.valueChanges.pipe(
      startWith(this.formGroup.value),
      tap(() => {

        this.dialog.submitButtonEnabled$.next(this.formGroup.valid && this.userCanEdit$.value);
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe();

    this.submitPressed$.pipe(
      filter(() => this.formGroup.valid),
      withLatestFrom(this.currentUser$),
      tap(([_, currentUser]) => {
        const request: SoftwareRequest = {
          id: this.requestId,
          updatedById: currentUser.id,
          updatedOn: new Date(),
          description: this.formGroup.value.description || '',
          title: this.formGroup.value.title || '',
          createdOn: !!this.args.softwareRequest ? this.args.softwareRequest!.createdOn : new Date(),
          createdById: !!this.args.softwareRequest ? this.args.softwareRequest!.createdById : currentUser.id,
          status: !!this.args.softwareRequest ? this.args.softwareRequest.status : SoftwareRequestStatus.Waiting,
          softwareTaskId: '',
          rejectionReason: '',
        }

        const returnArgs: EditSoftwareRequestDialogReturnValue = {
          softwareRequest: request
        }

        const returnValue: DialogReturn<EditSoftwareRequestDialogReturnValue> = {
          type: DialogReturnType.SUBMIT,
          value: returnArgs
        }
        this.dialog.submit$.next(returnValue);
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public filesUploaded(files: UploadedFile[]) {
    this.files$.next([
      ...this.files$.value,
      ...files,
    ])
  }

  public fileRemoved(fileId: string) {
    this.files$.next(this.files$.value.filter(f => f.id !== fileId));
  }

  public downloadFile(uploadedFile: UploadedFile) {
    const sanitizedName = uploadedFile.name.split('.')[0].replace(/[^a-zA-Z0-9]/g, '_')
    this.http.get(uploadedFile.downloadUrl, { responseType: 'blob' }).pipe(
      filter(blob => !!blob),
      first(),
      tap((blob) => {
        const url = URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.download = sanitizedName
        link.click()

        URL.revokeObjectURL(url)

        this.snackbarService.showSnackbar(SnackbarStatus.SUCCESS, "File downloaded")
      })
    ).subscribe()
  }

  removeFile(event: MouseEvent, fileId: string) {
    event.stopPropagation()
    this.files$.next(this.files$.value.filter(f => f.id !== fileId))
    this.snackbarService.showSnackbar(SnackbarStatus.SUCCESS, "File removed")
  }

  public async handleMarkdownPaste(
    event: ClipboardEvent,
    inputId: string,
  ) {
    const items = event.clipboardData?.items

    if (!items) {
      return;
    }
    const item = items[0]

    if (!item.type.includes('image')) {
      return;
    }

    const inputRef = this.elementRef.nativeElement.querySelector(`#${inputId}`)
    const textareaValue = inputRef?.value || ''
    const fileSuffix = item.type.split('/')[1]
    const fileId = this.appwrite.createId()
    const fileName = `${fileId}/${fileSuffix}`
    const filePath = `${StorageFolder.softwareTasks}/${this.requestId}/${fileName}`

    const blob = await resizeImage(item.getAsFile() as File, 800)
    from(this.storage.upload(filePath, blob).snapshotChanges()).pipe(
      map(snapshot => snapshot as CompatUploadSnapshot),
      filter(({ state }: CompatUploadSnapshot) => state === 'success'),
      first(),
      switchMap((imageUploadTask) => imageUploadTask.ref.getDownloadURL()),
      map((downloadUrl) => {

        const cursorPos = inputRef?.selectionStart || 0;
        const markdownImage = `![${fileId}](${downloadUrl},400,${fileId})`;
        const textareaValue = inputRef?.value || '';
        const newValue =
          textareaValue.slice(0, cursorPos) +
          markdownImage +
          textareaValue.slice(cursorPos);

        return newValue;
        // return textareaValue + `![${fileId}](${downloadUrl},400,${fileId})`
      }),
      tap((markdown) => {
        this.formGroup.controls.description.patchValue(markdown)
      })
    ).subscribe()
  }

  public onMarkdownDrop(
    event: DragEvent,
    inputId: string,
  ) {

    event.preventDefault()
    event.stopPropagation()

    if (!event.dataTransfer) {
      return;
    }

    if (!event.dataTransfer.items.length) {
      return;
    }

    const file = event.dataTransfer.files[0]

    const allowedImageFileTypes = [
      'image/png',
      'image/jpeg',
      'image/gif',
    ]
    const videoFileType = 'video/mp4'

    const allowedFileTypes = [
      ...allowedImageFileTypes,
      videoFileType
    ]

    if (!allowedFileTypes.includes(file.type)) {
      this.uploadNonImageFiles(Array.from(event.dataTransfer.files))
      return;
    }

    const [fileName, fileExt] = file.name.split('.')

    const uploadPipe = pipe(
      map(snapshot => snapshot as CompatUploadSnapshot),
      tap(console.log),
      filter(({ state }: CompatUploadSnapshot) => state === 'success')
    )

    if (allowedFileTypes.includes(file.type)) {
      // const blob = of(file)
      const blob = file.type === 'image/gif' ? of(file) : resizeImage(file, 1000)
      from(blob).pipe(
        first(),
        switchMap((resizedFile: Blob) => {
          const fileId = this.appwrite.createId();
          const fileName = `${fileId}.${fileExt}`
          const filePath = `${StorageFolder.softwareTasks}/${this.requestId}/${fileName}`
          return from(this.storage.upload(filePath, resizedFile).snapshotChanges()).pipe(
            uploadPipe,
            switchMap((imageUploadTask) => imageUploadTask.ref.getDownloadURL()),
            map((downloadUrl) => {
              const inputRef = this.elementRef.nativeElement.querySelector(`#${inputId}`)
              const textareaValue = inputRef?.value || '';
              return textareaValue + `![${fileId}](${downloadUrl},400,${fileId})`
            }),
            tap((markdown) => {
              this.formGroup.controls.description.patchValue(markdown)
            })
          )
        })
      ).subscribe()
    } else {

    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  uploadNonImageFiles(files: File[]) {

    const uploadPipe = pipe(
      map(snapshot => snapshot as CompatUploadSnapshot),
      filter(({ state }: CompatUploadSnapshot) => state === 'success')
    )

    const uploadedFiles$ = forkJoin(
      files.map((file) => {
        const fileId = this.appwrite.createId()
        const fileExt = file.name.split('.')[1]
        const filePath = `${StorageFolder.softwareTasks}/${this.requestId}/${fileId}.${fileExt}`
        return from(this.storage.upload(filePath, file).snapshotChanges().pipe(
          uploadPipe,
          switchMap((uploadTask) => uploadTask.ref.getDownloadURL()),
          map((downloadUrl) => {
            const uploadedFile: UploadedFile = {
              id: fileId,
              name: file.name,
              downloadUrl,
              thumbnailDownloadUrl: '',
              type: UploadedFileType.Other,
              active: true
            }

            return uploadedFile
          })
        ))
      })
    )

    uploadedFiles$.pipe(
      defaultIfEmpty([]),
      first(),
      tap((uploadedFiles) => {
        this.files$.next([...this.files$.value, ...uploadedFiles])
        this.snackbarService.showSnackbar(SnackbarStatus.SUCCESS, "Files attached")
      })
    ).subscribe()



  }

  public updateMarkdown(
    inputId: string,
    newValue?: string,
  ) {
    const inputRef = this.elementRef.nativeElement.querySelector(`#${inputId}`)
    let value = inputRef.value || ''
    if (!!newValue) {
      inputRef.value = newValue
      value = newValue
    }

    this.formGroup.controls.description.patchValue(value)
  }
}
